import {defineStore, storeToRefs} from "pinia";
import {computed, ref} from "vue";
import {useCompanyStore} from "@/modules/company/store/company";
import {
    Coupon,
    DeliveryCalculate,
    DeliveryComputed,
    DeliveryPriceRule,
    Order,
    OrderAttribute,
    OrderAttributeCodeEnum,
    OrderAttributesValue,
    OrderProfileAttributesValue,
    OrderRestriction,
    PaymentType,
    TransportType
} from "@/gql/gen";
import {arrayIndex} from "@/core/util/arrayIndex";
import parseTime from "@/core/util/date/parseTime";
import {canDeliverForAttrs, compareAttrs, getDeliveryPriceForAttrs, VorderTab} from "@/modules/shop/store/util/vorder";
import {DeliveryCalculateEntry} from "@/modules/shop/store/util/delivery";

const STORE_NAME = 'vorder-state'

export const useVorderState = defineStore(STORE_NAME, () => {

    const companyStore = useCompanyStore()

    const {officesById, offices} = storeToRefs(companyStore);

    const tab = ref<VorderTab>("basket");

    const orderRestrictions = ref<OrderRestriction[]>([]);

    const attrs = ref<OrderAttributesValue>({} as OrderAttributesValue)
    const attrsPrev = ref<OrderAttributesValue>({} as OrderAttributesValue)
    const attrsInfo = ref<OrderAttribute[]>([]);

    const vorderId = ref(0);

    const transportTypes = ref<TransportType[]>([]);
    const deliveries = ref<DeliveryComputed[]>([]);

    const coupon = ref<Coupon | null>();
    const paymentTypes = ref<PaymentType[]>([]);
    const deliveryPriceRules = ref<DeliveryPriceRule[]>([]);

    const order = ref<Order>();

    const vorderSynced = ref(false);

    // ATTRS
    const currentProfileId = computed(() => attrs.value.PROFILE_ID);

    const attrByCode = computed(() => {
        return attrsInfo.value.reduce<{
            [key in OrderAttributeCodeEnum]?: OrderAttribute;
        }>((map, item) => {
            map[item.CODE] = item;
            return map;
        }, {});
    });

    // BENEFIT

    const bonuses = computed(() => attrs.value.BONUSES || 0);

    const benefitType = computed(() => attrs.value.BENEFIT_TYPE)

    // PAYMENT

    const currentPaymentType = computed(() => {
        return paymentTypes.value.find(
            (item) => item.CODE === attrs.value.PAYMENT_TYPE
        );
    });

    // DELIVERY
    const deliveryById = computed(() => arrayIndex(deliveries.value, "ID"));

    const currentDeliveryId = computed(() => attrs.value.DELIVERY_ID);

    const currentDelivery = computed(() => currentDeliveryId.value ? deliveryById.value[currentDeliveryId.value] : null);

    const isDeliveryPickup = computed(() => {
        return attrs.value.TRANSPORT_TYPE === 'pickup'
    });

    const isDeliveryCourier = computed(() => {
        return attrs.value.TRANSPORT_TYPE === 'courier'
    });

    const deliveryCanDeliver = computed(() => canDeliverForAttrs(attrs.value));

    const deliveryPriceForCurrentAttrs = computed(() => getDeliveryPriceForAttrs(attrs.value));

    const deliveryDate = computed(() => {
        if (attrs.value.DATE && attrs.value.TIME) {
            return parseTime(
                attrs.value.DATE + " " + attrs.value.TIME,
                "datetime",
                "dayjs"
            );
        }
    });

    const deliveryDateFormatted = computed(() => {
        return deliveryDate.value?.format('DD.MM.YYYY HH:mm')
    });

    // DELIVERY CALC
    const deliveryCalculateResult = ref<DeliveryCalculate | null | undefined>()
    const deliveryCalculatesMap = ref<Record<string, DeliveryCalculateEntry>>({});


    // OFFICES
    const saleOffices = computed(() => {
        return offices.value.filter(item => item)
    })

    const pickupOffices = computed(() => {
        return saleOffices.value.filter(item => item.PROPERTIES.ROLES.find(role => role.CODE === 'pickup'))
    })

    const currentPickupOfficeId = computed(() => attrs.value.PICKUP_DEPARTMENT);
    const currentCourierOfficeId = computed(() => attrs.value.DELIVERY_DEPARTMENT);

    const currentPickupOffice = computed(() => {
        return currentPickupOfficeId.value ? officesById.value[currentPickupOfficeId.value] : null
    });

    const orderOfficeId = computed(() => {
        if (isDeliveryPickup.value) {
            return attrs.value.PICKUP_DEPARTMENT
        } else {
            return attrs.value.DELIVERY_DEPARTMENT
        }
    });

    const orderOffice = computed(() => {
        if (orderOfficeId.value) {
            return officesById.value[orderOfficeId.value]
        }
    });

    // METHODS

    function updateAttrs(attrValues: Partial<OrderAttributesValue> | Partial<OrderProfileAttributesValue>) {
        const compare = compareAttrs(attrs.value, attrValues as OrderAttributesValue)
        if (compare.isChanged) {
            attrs.value = {
                ...attrs.value,
                ...compare.changedValues,
            };
        }
        return compare
    }

    const setAttr = <
        T extends keyof OrderAttributesValue = keyof OrderAttributesValue
    >(
        code: T,
        value: OrderAttributesValue[T]
    ) => {
        attrs.value[code] = value;
    };


    return {
        vorderId,
        attrs,
        attrsPrev,
        attrsInfo,
        order,
        paymentTypes,
        transportTypes,
        deliveries,
        deliveryById,
        bonuses,
        isDeliveryCourier,
        isDeliveryPickup,
        orderOfficeId,
        orderOffice,
        vorderSynced,
        currentPaymentType,
        currentProfileId,
        currentPickupOfficeId,
        deliveryCanDeliver,
        deliveryPriceForCurrentAttrs,
        currentPickupOffice,
        saleOffices,
        deliveryDate,
        currentDelivery,
        coupon,
        attrByCode,
        updateAttrs,
        setAttr,
        benefitType,
        deliveryCalculateResult,
        deliveryCalculatesMap,
        tab,
        orderRestrictions,
        deliveryPriceRules,
        deliveryDateFormatted,
        pickupOffices
    }
}, {
    persist: {
        pick: [
            'vorderId',
            'attrs',
            'coupon',
            'deliveryCalculateResult'
        ],
    }
})

